import Vue from "vue";
import VueRouter from "vue-router";
import { checkPermission } from "@/utils/common";
import configServices from "@/services/config";
import constants, { ROLE_ACTIONS, ROLE_ACTION_METHODS, GROUP_KEYS } from "@/config/constants";

const allConfigs = configServices.getAllConfigs();
const menus = allConfigs.filter((c) => c.type !== "categories");

// Layout
import Layout from "@/layout";
import ErrorLayout from "@/layout/error";
import AuthLayout from "@/layout/auth";

Vue.use(VueRouter);

const errors = {
  key: "errors",
  path: "/errors",
  layout: "error",
  name: "Errors",
  redirect: "/errors/404",
  children: [
    {
      path: "403",
      name: "Forbidden",
      component: "403",
    },
    {
      path: "404",
      name: "PageNotFound",
      component: "404",
    },
    {
      path: "500",
      name: "ServerError",
      component: "500",
    },
    {
      path: "502",
      name: "BadGateway",
      component: "502",
    },
  ],
};

const authRouters = [
  {
    path: "/login",
    component: AuthLayout,
    children: [
      {
        path: "",
        name: "Login",
        component: () => import("@/views/login/index"),
        meta: { auth: false, title: "Đăng nhập" },
      },
    ],
  },
];

const errorRouters = [errors].map((r) => ({
  ...r,
  component: ErrorLayout,
  children: r.children.map((c) => ({
    ...c,
    component: () => import(`@/views/errors/${c.component}`),
  })),
}));

const dashboardRouters = [{
  path: "/",
  component: Layout,
  layout: "root",
  children: [
    {
      path: "",
      name: "Dashboard",
      component: () => import("@/views/dashboard/index"),
      meta: {
        auth: true,
        title: "Trang chủ",
        icon: "el-icon-menu",
        bottom: true,
        group: GROUP_KEYS.DASHBOARD
      },
    },
  ],
}];

const menuRouters = (() => {
  const results = [];
  menus.forEach(r => {
    results.push({
      path: `/${r.extra.key.replaceAll("_", "-")}`,
      component: Layout,
      layout: "root",
      children: [
        {
          path: "",
          name: r.extra.key,
          component: () => import(`@/views/shared/index`),
          meta: {
            auth: true,
            title: r.extra?.info?.menu || r.extra.name,
            icon: r.extra?.info?.icon || "",
            bottom: true,
            configId: r.id,
            role_action: constants.OBJECT_TYPES.find((t) => t.value == r.type)?.role_action,
            role_action_method: ROLE_ACTION_METHODS.READ,
            group: r.extra.key
          },
        },
      ],
    });
    if (r.extra.modules?.length > 0) {
      r.extra.modules.forEach(module => {
        results.push({
          path: `/${r.extra.key.replaceAll("_", "-")}/${module.key.replaceAll("_", "-")}`,
          component: Layout,
          layout: "root",
          children: [
            {
              path: "",
              name: `${r.extra.key}__${module.key}`,
              component: () => import(`@/views/shared/index`),
              meta: {
                auth: true,
                title: module.name,
                icon: module.icon || "",
                bottom: true,
                configId: r.id,
                module: module,
                role_action: constants.OBJECT_TYPES.find((t) => t.value == r.type)?.role_action,
                role_action_method: ROLE_ACTION_METHODS.READ,
                group: r.extra.key
              },
            },
          ],
        });
      });
    }
  });
  return results;
})()

const menuDetailRouters = (() => {
  const results = [];
  menus.forEach((r) => {
    results.push({
      path: `/${r.extra.key.replaceAll("_", "-")}/:id`,
      component: Layout,
      children: [
        {
          path: "",
          name: `${r.extra.key}_show`,
          component: () => import(`@/views/shared/show`),
          meta: {
            auth: true,
            title: `Cập nhật thông tin ${r.extra.name}`,
            configId: r.id,
            role_action: constants.OBJECT_TYPES.find((t) => t.value == r.type)?.role_action,
            role_action_method: ROLE_ACTION_METHODS.READ,
            parent: r.extra.key,
          },
        },
      ],
    });
    if (r.extra.modules?.length > 0) {
      r.extra.modules.forEach(module => {
        results.push({
          path: `/${r.extra.key.replaceAll("_", "-")}/${module.key.replaceAll("_", "-")}/:id`,
          component: Layout,
          children: [
            {
              path: "",
              name: `${r.extra.key}__${module.key}_show`,
              component: () => import(`@/views/shared/show`),
              meta: {
                auth: true,
                title: `Cập nhật thông tin ${r.extra.name}`,
                configId: r.id,
                module: module,
                role_action: constants.OBJECT_TYPES.find((t) => t.value == r.type)?.role_action,
                role_action_method: ROLE_ACTION_METHODS.READ,
                parent: `${r.extra.key}__${module.key}`,
              },
            },
          ],
        });
      })
    }
  })
  return results;
})()

const menuViewRouters = (() => {
  const results = [];
  menus.forEach((r) => {
    results.push({
      path: `/${r.extra.key.replaceAll("_", "-")}/:id/view`,
      component: Layout,
      children: [
        {
          path: "",
          name: `${r.extra.key}_view`,
          component: () => import(`@/views/shared/view`),
          meta: {
            auth: true,
            title: `Xem chi tiết ${r.extra.name}`,
            configId: r.id,
            role_action: constants.OBJECT_TYPES.find((t) => t.value == r.type)?.role_action,
            role_action_method: ROLE_ACTION_METHODS.READ,
            parent: r.extra.key,
          },
        },
      ],
    });
    if (r.extra.modules?.length > 0) {
      r.extra.modules.forEach(module => {
        results.push({
          path: `/${r.extra.key.replaceAll("_", "-")}/${module.key.replaceAll("_", "-")}/:id/view`,
          component: Layout,
          children: [
            {
              path: "",
              name: `${r.extra.key}__${module.key}_view`,
              component: () => import(`@/views/shared/view`),
              meta: {
                auth: true,
                title: `Xem chi tiết ${r.extra.name}`,
                configId: r.id,
                module: module,
                role_action: constants.OBJECT_TYPES.find((t) => t.value == r.type)?.role_action,
                role_action_method: ROLE_ACTION_METHODS.READ,
                parent: `${r.extra.key}__${module.key}`,
              },
            },
          ],
        });
      })
    }
  })
  return results;
})()

const menuConnectionRouters = (() => {
  const results = [];
  menus.forEach((r) => {
    results.push({
      path: `/${r.extra.key.replaceAll("_", "-")}/:id/connection`,
      component: Layout,
      children: [
        {
          path: "",
          name: `${r.extra.key}_connection`,
          component: () => import(`@/views/shared/connection`),
          meta: {
            auth: true,
            title: `Danh sách liêt kết ${r.extra.name}`,
            configId: r.id,
            role_action: constants.OBJECT_TYPES.find((t) => t.value == r.type)?.role_action,
            role_action_method: ROLE_ACTION_METHODS.READ,
            parent: r.extra.key,
          },
        },
      ],
    });
    if (r.extra.modules?.length > 0) {
      r.extra.modules.forEach(module => {
        results.push({
          path: `/${r.extra.key.replaceAll("_", "-")}/${module.key.replaceAll("_", "-")}/:id/connection`,
          component: Layout,
          children: [
            {
              path: "",
              name: `${r.extra.key}__${module.key}_connection`,
              component: () => import(`@/views/shared/connection`),
              meta: {
                auth: true,
                title: `Danh sách liêt kết ${r.extra.name}`,
                configId: r.id,
                module: module,
                role_action: constants.OBJECT_TYPES.find((t) => t.value == r.type)?.role_action,
                role_action_method: ROLE_ACTION_METHODS.READ,
                parent: `${r.extra.key}__${module.key}`,
              },
            },
          ],
        });
      })
    }
  })
  return results;
})()

const commonRouters = [
  {
    path: "/categories",
    component: Layout,
    layout: "root",
    children: [
      {
        path: "",
        name: "Categories",
        component: () => import("@/views/categories/index"),
        meta: {
          auth: true,
          title: "Quản lý danh mục",
          icon: "el-icon-folder",
          bottom: true,
          role_action: ROLE_ACTIONS.CATEGORY,
          role_action_method: ROLE_ACTION_METHODS.READ,
          group: GROUP_KEYS.CATEGORIES
        },
      },
    ],
  },
  {
    path: "/approved-documents",
    component: Layout,
    layout: "root",
    children: [
      {
        path: "",
        name: "ApprovedDocuments",
        component: () => import("@/views/approved-documents/index"),
        meta: {
          auth: true,
          title: "Quy trình phê duyệt",
          icon: "el-icon-finished",
          bottom: true,
          role_action: ROLE_ACTIONS.APPROVE_DOCUMENT,
          role_action_method: ROLE_ACTION_METHODS.READ,
          group: GROUP_KEYS.APPROVED_DOCUMENTS
        },
      },
    ],
  },
  {
    path: "/roles",
    component: Layout,
    layout: "root",
    children: [
      {
        path: "",
        name: "Roles",
        component: () => import("@/views/roles/index"),
        meta: {
          auth: true,
          title: "Quyền hạn",
          icon: "el-icon-s-management",
          bottom: true,
          role_action: ROLE_ACTIONS.MANAGEMENT_ROLE,
          role_action_method: ROLE_ACTION_METHODS.READ,
          group: GROUP_KEYS.MANAGE
        },
      },
    ],
  },
  {
    path: "/users",
    component: Layout,
    layout: "root",
    children: [
      {
        path: "",
        name: "UserRoles",
        component: () => import("@/views/role-users/index"),
        meta: {
          auth: true,
          title: "Quản lý tài khoản",
          icon: "el-icon-collection",
          bottom: true,
          role_action: ROLE_ACTIONS.MANAGEMENT_ROLE_USER,
          role_action_method: ROLE_ACTION_METHODS.READ,
          group: GROUP_KEYS.MANAGE
        },
      },
    ],
  },
  {
    path: "/logs",
    component: Layout,
    layout: "root",
    children: [
      {
        path: "",
        name: "Logs",
        component: () => import("@/views/logs/index"),
        meta: {
          auth: true,
          title: "Lịch sử thao tác",
          icon: "el-icon-time",
          bottom: true,
          role_action: ROLE_ACTIONS.MANAGEMENT_LOG,
          role_action_method: ROLE_ACTION_METHODS.READ,
          group: GROUP_KEYS.LOG
        },
      },
    ],
  },
  {
    path: "/requests",
    component: Layout,
    layout: "root",
    children: [
      {
        path: "",
        name: "Requests",
        component: () => import("@/views/requests/index"),
        meta: {
          auth: true,
          title: "Lịch sử phê duyệt",
          icon: "el-icon-notebook-2",
          bottom: true,
          role_action: ROLE_ACTIONS.EXTRA,
          role_action_method: ROLE_ACTION_METHODS.EXTRA,
          group: GROUP_KEYS.LOG
        },
      },
    ],
  },
  {
    path: "/files",
    component: Layout,
    layout: "root",
    children: [
      {
        path: "",
        name: "Files",
        component: () => import("@/views/files/index"),
        meta: {
          auth: true,
          title: "Quản lý Media",
          icon: "el-icon-files",
          bottom: true,
          role_action: ROLE_ACTIONS.FILE,
          role_action_method: ROLE_ACTION_METHODS.READ,
          group: GROUP_KEYS.OTHER
        },
      },
    ],
  },
  {
    path: "/tags",
    component: Layout,
    layout: "root",
    children: [
      {
        path: "",
        name: "Tags",
        component: () => import("@/views/tags/index"),
        meta: {
          auth: true,
          title: "Quản lý Tags",
          icon: "el-icon-collection-tag",
          bottom: true,
          role_action: ROLE_ACTIONS.TAG,
          role_action_method: ROLE_ACTION_METHODS.READ,
          group: GROUP_KEYS.OTHER
        },
      },
    ],
  },
  {
    path: "/report",
    component: Layout,
    layout: "root",
    children: [
      {
        path: "",
        name: "Report",
        component: () => import("@/views/report/index"),
        meta: {
          auth: true,
          title: "Báo cáo",
          icon: "el-icon-document-copy",
          bottom: true,
          role_action: ROLE_ACTIONS.REPORT_HANDLING,
          role_action_method: ROLE_ACTION_METHODS.READ,
          group: GROUP_KEYS.OTHER
        },
      },
    ],
  },
];

const permissionRouters = [
  ...menuRouters,
  ...menuDetailRouters,
  ...menuViewRouters,
  ...menuConnectionRouters,
  ...commonRouters,
].filter((r) => {
  const children = r.children.filter((c) => checkPermission(c.meta.role_action, c.meta.role_action_method));
  return children.length > 0
})

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes: [
    ...authRouters,
    ...dashboardRouters,
    ...permissionRouters,
    ...errorRouters,
    { path: "*", redirect: "/errors/404", hidden: true },
  ],
});

export default router;
